import { useState, useEffect, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useAuthContext } from "contexts/AuthContext"
import useDictionaryApi from "modules/dictionary"
import { languages as staticLanguages } from "utils/data/languages"

const normalizeStaticLanguages = () => {
  return staticLanguages.map((lang) => ({
    languageId: lang.id,
    name: lang.name,
    code: lang.id === 1 ? "es" : lang.id === 3 ? "fr" : "en",
  }))
}

export const useLanguageSelector = () => {
  const { i18n } = useTranslation()
  const { checkLanguage } = useAuthContext()
  const { getLanguages } = useDictionaryApi()

  const [anchorEl, setAnchorEl] = useState(null)
  const [languages, setLanguages] = useState(normalizeStaticLanguages())
  const open = Boolean(anchorEl)

  useEffect(() => {
    let active = true
    const fetchLanguages = async () => {
      try {
        const apiLanguages = await getLanguages()
        if (active && apiLanguages && apiLanguages.length > 0) {
          setLanguages(apiLanguages)
        }
      } catch (error) {
        console.warn(
          "Failed to fetch languages from API, using static fallbacks.",
          error
        )
      }
    }
    fetchLanguages()
    return () => {
      active = false
    }
  }, [getLanguages])

  const activeCode = (i18n.language || "en").toLowerCase()

  const currentLanguage =
    languages.find((lang) => {
      const langCode = (lang.code || "").toLowerCase()
      if (!langCode) return false
      // Exact match (e.g., 'en-US' === 'en-US' or 'en' === 'en')
      if (activeCode === langCode) return true
      // Match by prefix (e.g. activeCode 'en-US' vs langCode 'en', or 'en' vs 'en-US')
      const activePrefix = activeCode.split("-")[0]
      const langPrefix = langCode.split("-")[0]
      return activePrefix === langPrefix
    }) ||
    languages.find((lang) => lang.languageId === 2) ||
    languages[0]

  const handleOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleSelectLanguage = useCallback(
    async (languageId) => {
      try {
        await checkLanguage(languageId)
      } catch (error) {
        console.error("Failed to change language:", error)
      }
    },
    [checkLanguage]
  )

  return {
    currentLanguageName: currentLanguage?.name,
    languages,
    open,
    anchorEl,
    handleOpen,
    handleClose,
    handleSelectLanguage,
  }
}
